import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, FlaskConical, BookOpen } from "lucide-react";
import PageHero from "@/components/PageHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import { Reveal, Eyebrow } from "@/components/Motion";
import { DOCTOR, IMAGES, TIMELINE, EDITORIAL_ROLES } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const About = () => {
  const { t, lang } = useLang();
  return (
  <main data-testid="about-page">
    <PageHero
      key={lang}
      eyebrow={t("ab_hero_eyebrow")}
      lines={[t("ab_hero_l1"), t("ab_hero_l2")]}
      testId="about-hero"
    >
      {t("ab_hero_sub")}
    </PageHero>

    {/* PROFILE + QUALIFICATIONS */}
    <section data-testid="profile-section" className="bg-white border-y border-mist py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <div className="lg:sticky lg:top-32 self-start">
            <img
              src={IMAGES.portrait}
              alt={DOCTOR.name}
              data-testid="about-portrait"
              className="w-full h-auto object-contain object-top block"
            />
          </div>
        </Reveal>

        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>{t("ab_qual_eyebrow")}</Eyebrow>
            <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950 leading-[1.05]">
              {t("ab_qual_title1")} <span className="italic text-pine-500">{t("ab_qual_title2")}</span>
            </h2>
          </Reveal>
          <div className="mt-10 divide-y divide-mist border-y border-mist" data-testid="qualifications-list">
            {DOCTOR.qualifications.map((q, i) => (
              <Reveal key={q} delay={i * 0.06}>
                <div className="py-5 flex items-start gap-4">
                  <GraduationCap size={18} className="mt-1 shrink-0 text-pine-500" />
                  <span className="text-pine-900 font-medium">{q}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10 space-y-5 text-sage leading-relaxed" data-testid="bio-narrative">
              <p>
                <span className="font-serif text-6xl leading-[0.85] float-left mr-3 mt-1 text-pine-500">DR.</span>
                {t("ab_bio1")}
              </p>
              <p>{t("ab_bio2")}</p>
              <p>{t("ab_bio3")}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* TIMELINE */}
    <section data-testid="timeline-section" className="bg-bone py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow>{t("ab_journey_eyebrow")}</Eyebrow>
          <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950 leading-[1.05]">
            A <span className="italic text-pine-500">{t("ab_journey_title").replace(/^A /, "")}</span>
          </h2>
        </Reveal>

        <div className="mt-16 relative" data-testid="career-timeline">
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-pine-200" />
          <div className="space-y-12">
            {TIMELINE.map((item, i) => (
              <Reveal key={item.role + item.place} delay={i * 0.06}>
                <div className="relative pl-12" data-testid={`timeline-item-${i}`}>
                  <span className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-pine-500 bg-bone" />
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-pine-500">
                    {t(`tl${i + 1}_p`)}
                  </div>
                  <h3 className="mt-2 font-serif text-2xl text-pine-950">{t(`tl${i + 1}_r`)}</h3>
                  <p className="mt-1 text-sm text-sage leading-relaxed max-w-2xl">{item.place}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <EditorialMarquee dark={false} />

    {/* RESEARCH */}
    <section data-testid="research-section" className="bg-bone py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>{t("ab_research_eyebrow")}</Eyebrow>
            <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950 leading-[1.05]">
              {t("ab_research_title1")} <span className="italic text-pine-500">{t("ab_research_title2")}</span>
            </h2>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 bg-pine-950 text-bone p-10 lg:p-12 relative overflow-hidden" data-testid="infinity-study-card">
              <FlaskConical size={120} className="absolute -right-6 -bottom-6 text-pine-800" strokeWidth={1} />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pine-300">
                  {t("ab_infinity_label")}
                </div>
                <h3 className="mt-4 font-serif text-3xl leading-tight">
                  {t("ab_infinity_title")}
                </h3>
                <p className="mt-4 text-pine-100/80 text-sm leading-relaxed max-w-lg">
                  {t("ab_infinity_body")}
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.2}>
            <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-pine-500 flex items-center gap-3">
              <span className="h-px w-8 bg-pine-500" /> {t("ab_editorial_label")}
            </h3>
          </Reveal>
          <div className="mt-8 space-y-5" data-testid="editorial-roles">
            {EDITORIAL_ROLES.map((r, i) => (
              <Reveal key={r.journal} delay={0.25 + i * 0.08}>
                <div className="bg-white border border-mist p-6 flex items-start gap-4 hover:border-pine-500/40 transition-colors duration-500">
                  <BookOpen size={18} className="mt-1 shrink-0 text-pine-500" />
                  <div>
                    <div className="font-semibold text-pine-950 text-sm">{r.role}</div>
                    <div className="text-sm text-sage mt-1">{r.journal}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section data-testid="about-cta" className="bg-white border-t border-mist py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <Reveal>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-pine-950 leading-[1.05]">
            {t("ab_cta_title1")}<br />{t("ab_cta_title2")}
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/book"
            data-testid="about-book-button"
            className="group inline-flex items-center gap-2 bg-pine text-bone font-semibold text-sm px-8 py-4 rounded-full hover:bg-pine-500 transition-colors duration-300"
          >
            {t("cta_book_btn")}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
  );
};

export default About;
